import React from 'react';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import PropTypes from 'prop-types';
import Select from 'components/core/Select';
import _get from 'lodash/get';
import { sanitizeFloat } from 'utils/Tools';
import NumberInputField from 'components/inputFields/NumberInputField';

const REASONS = [
  { value: 'BILLING_ERROR', label: 'refund_reason.billing_error' },
  { value: 'CANCELLATION', label: 'refund_reason.cancellation' },
  { value: 'EXCEPTIONAL_CASE', label: 'refund_reason.exceptional_case' },
  { value: 'GOODWILL_GESTURE', label: 'refund_reason.goodwill_gesture' },
  { value: 'TECHNICAL_ISSUE', label: 'refund_reason.technical_issue' },
  { value: 'UNAVAILABLE_SPACE', label: 'refund_reason.unavailable_space' },
];

const REFUND_TYPES = [
  { value: 'TOTAL', label: 'refund_type.total' },
  { value: 'PARTIAL_PERCENT', label: 'refund_type.partial.percent' },
  { value: 'PARTIAL_AMOUNT', label: 'refund_type.partial.amount' },
];

const formatStringToDecimal = (value) => (typeof value === 'string' ? +(value.replace(',', '.').replace(/\.$/, '')) : value);
const isNumber = (value) => typeof value === 'number' && !Object.is(value, NaN);

class PurchaseRefund extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);

    this.state = {
      refundReason: null,
      refundType: null,
      refundAmount: 0,
      refundPercent: 0,
      error: null,
    };
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate = () => {
    const {
      onValidate,
    } = this.props;
    const {
      refundReason, refundType, refundPercent, refundAmount,
    } = this.state;

    // ❌ No reason
    if (!refundReason) {
      this.setState({
        error: {
          field: 'refund_reason',
          label: 'error.purchase.refundReasonRequired',
        },
      });
      return false;
    }

    // ❌ No type
    if (!refundType) {
      this.setState({
        error: {
          field: 'refund_type',
          label: 'error.purchase.refundTypeRequired',
        },
      });
      return false;
    }

    // 🔁 Format number for decimal cases
    const formattedRefundPercent = formatStringToDecimal(refundPercent);
    const formattedRefundAmount = formatStringToDecimal(refundAmount);
    this.setState({
      refundAmount: formattedRefundAmount,
      refundPercent: formattedRefundPercent,
    });

    // ❌ Partial refund in % but percent is 0%
    if (refundType === 'PARTIAL_PERCENT' && (!formattedRefundPercent || (isNumber(formattedRefundPercent) && formattedRefundPercent === 0))) {
      this.setState({
        error: {
          field: 'refund_percent',
          label: 'error.purchase.percentMustBeHigherThanZero',
        },
      });
      return false;
    }

    // ❌ Partial refund in % but percent is >100%
    if (refundType === 'PARTIAL_PERCENT' && (!formattedRefundPercent || (isNumber(formattedRefundPercent) && formattedRefundPercent > 100))) {
      this.setState({
        error: {
          field: 'refund_percent',
          label: 'error.purchase.percentMustBeEqualOrLowerThanHundred',
        },
      });
      return false;
    }

    // ❌ Partial refund in % but percent is not a number
    if (refundType === 'PARTIAL_PERCENT' && (formattedRefundPercent && !isNumber(formattedRefundPercent))) {
      this.setState({
        error: {
          field: 'refund_percent',
          label: 'error.purchase.percentMustBeAPositiveNumber',
        },
      });
      return false;
    }

    // ❌ Partial refund but amount is 0
    if (refundType === 'PARTIAL_AMOUNT' && (!formattedRefundAmount || (isNumber(formattedRefundAmount) && formattedRefundAmount === 0))) {
      this.setState({
        error: {
          field: 'refund_amount',
          label: 'error.purchase.amountMustBeHigherThanZero',
        },
      });
      return false;
    }

    // ❌ Partial refund but amount is higher than purchase total price
    const totalAmountInclTaxInCentimes = _get(this, 'props.purchase.totalAmountInclTax', null);
    if (refundType === 'PARTIAL_AMOUNT' && isNumber(formattedRefundAmount) && formattedRefundAmount > totalAmountInclTaxInCentimes / 100) {
      this.setState({
        error: {
          field: 'refund_amount',
          label: 'error.purchase.amountMustBeEqualOrLowerThanPurchasePrice',
        },
      });
      return false;
    }

    // ✅
    const input = { reason: this.state.refundReason, refundValue: {} };
    if (refundType === 'TOTAL') delete input.refundValue;
    if (refundType === 'PARTIAL_PERCENT') input.refundValue.percentage = formattedRefundPercent * 100;
    if (refundType === 'PARTIAL_AMOUNT') input.refundValue.amount = formattedRefundAmount * 100;

    onValidate(input);
    return true;
  };

  /* Améliorations erreurs backend ?
  refundValue.percentage < 0    => erreur non spécifiée (proposition: "Invalid percentage" ou "must be greather than 0")
  refundValue.percentage = 0    => erreur 500 (proposition: "Invalid percentage"  ou "must be greather than")
  refundValue.percentage >10000 => erreur non spécifiée (proposition: "Invalid percentage"  ou "must be less than 100")

  pas de reason ou reason = null (ou autre data type) => erreur non spécifiée (proposition: "A refund reason is missing")
  percentage et amount en même temps => erreur non spécifiée (proposition: "Percentage and amount cannot be specified at same time")

  pas de carpark id => erreur non spécifiée (proposition: "No")
  */

  render() {
    const {
      refundReason,
      refundType,
      refundAmount,
      refundPercent,
      error,
    } = this.state;

    const {
      serverError,
    } = this.props;

    function extractError(field) {
      return error && _get(error, 'field') === field ? <LocalizedMessage id={_get(error, 'label', '')} /> : null;
    }

    const { parkingType } = this.props;

    // ❌ Errors
    const isNotRefundable = serverError === 'not_refundable';

    let serverErrorId = null;
    if (isNotRefundable) serverErrorId = 'field.purchase.error.not_refundable';

    // 1️⃣ Refund reason
    const SelectRefundReason = (
      <Select
        label={<LocalizedMessage id="field.selectRefundReason" />}
        value={refundReason}
        items={
            REASONS.map(({ value, label }) => ({
              key: value,
              value,
              label: <LocalizedMessage id={label} />,
            }))
          }
        errorText={extractError('refund_reason')}
        required
        onChange={(newReason) => this.setState({ refundReason: newReason, error: null })}
      />
    );

    // 2️⃣ Refund type
    const SelectRefundType = (
      refundReason ? (
        <Select
          label={<LocalizedMessage id="field.selectRefundType" />}
          value={refundType}
          items={
            REFUND_TYPES.map(({ value, label }) => ({
              key: value,
              value,
              label: <LocalizedMessage id={label} />,
            }))
          }
          errorText={extractError('refund_type')}
          required
          onChange={(newType) => this.setState({
            refundType: newType, refundAmount: 0, refundPercent: 0, error: null,
          })}
        />
      ) : null
    );

    // 🌐 Locale for numbers
    const locale = _get(this, 'props.locale', 'fr-FR');
    const currency = _get(this, 'props.purchase.priceRule.currency', 'EUR');
    const amountFormatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const formatAmount = (amount) => amountFormatter.format(amount);
    const currencySymbol = amountFormatter.formatToParts(1).find((part) => part.type === 'currency').value;
    const totalAmountInclTax = _get(this, 'props.purchase.totalAmountInclTax', null);
    const maxRefundableAmount = isNumber(totalAmountInclTax) ? (totalAmountInclTax / 100).toFixed(2) : null;
    const formattedRefundPercent = formatStringToDecimal(refundPercent);
    const computedRefundedAmountInCentimes = isNumber(totalAmountInclTax) ? totalAmountInclTax * (formattedRefundPercent / 100) : null;
    const computedRefundedAmount = isNumber(computedRefundedAmountInCentimes) ? (computedRefundedAmountInCentimes / 100).toFixed(2) : null;

    let RefundAmount = null;
    let RefundPercent = null;

    // Full refund
    if (refundType === 'TOTAL') {
      RefundAmount = (
        <div className="d-flex" style={{ gap: 15 }}>
          <div style={{ minWidth: '150px' }}>
            <NumberInputField
              label={<LocalizedMessage id="field.refundedAmount" />}
              min={0}
              value={formatAmount(maxRefundableAmount)}
              disabled
              inputMaxWidth={70}
            />
          </div>
        </div>
      );
    }

    // Refund percent %%%
    const changeRefundPercent = (changeInput) => {
      let formattedInput = changeInput;
      if (typeof changeInput === 'string') {
        // Remove non numbers
        formattedInput = changeInput.replace(/[^\d,.]/g, '');
        // Limit to 2 decimals
        formattedInput = formattedInput.replace(/(?<=\d+(\.|,)\d{2}).*$/, '');
      }
      this.setState({ refundPercent: formattedInput, error: null });
    };

    if (refundType === 'PARTIAL_PERCENT') {
      RefundPercent = (
        <div className="d-flex" style={{ gap: 15 }}>
          <div style={{ minWidth: '150px' }}>
            <NumberInputField
              label={<LocalizedMessage id="field.refundedPercent" />}
              min={0}
              value={refundPercent}
              adornments={{ start: '%' }}
              required
              errorText={extractError('refund_percent')}
              sanitizer={sanitizeFloat}
              inputMaxWidth={100}
              onChange={(value) => changeRefundPercent(value)}
            />
          </div>
          <div style={{ minWidth: '150px' }}>
            <NumberInputField
              label={<LocalizedMessage id="field.refundedAmount" />}
              min={0}
              value={formatAmount(computedRefundedAmount)}
              disabled
              inputMaxWidth={70}
            />
          </div>
        </div>
      );
    }

    // Refund amount €€€
    const changeRefundAmount = (changeInput) => {
      let formattedInput = changeInput;
      if (typeof changeInput === 'string') {
        // Remove non numbers
        formattedInput = changeInput.replace(/[^\d,.]/g, '');
        // Limit to 2 decimals
        formattedInput = formattedInput.replace(/(?<=\d+(\.|,)\d{2}).*$/, '');
      }
      this.setState({ refundAmount: formattedInput, error: null });
    };
    if (refundType === 'PARTIAL_AMOUNT') {
      RefundAmount = (
        <div style={{ minWidth: '200px' }}>
          <NumberInputField
            label={<LocalizedMessage id="field.refundedAmountWithMax" values={{ max: formatAmount(maxRefundableAmount) }} />}
            min={0}
            value={refundAmount}
            required
            errorText={extractError('refund_amount')}
            adornments={{ end: currencySymbol }}
            sanitizer={sanitizeFloat}
            inputMaxWidth={70}
            onChange={(value) => changeRefundAmount(value)}
          />
        </div>
      );
    }

    return (
      <div className="p-4">
        {/* Error message */}
        {serverErrorId && (
          <div style={{
            background: '#fee2e2', color: '#dc2626', fontWeight: '500', padding: 10, marginBottom: 20,
          }}
          >
            <LocalizedMessage id={serverErrorId} />
          </div>
        )}
        <LocalizedMessage id={parkingType === 'booking' ? 'description.booking.confirmRefund' : 'description.onDemand.confirmRefund'} />
        <div className="mt-3">
          <div style={{ maxWidth: '350px' }}>
            {SelectRefundReason}
          </div>
          <div className="d-flex w-100" style={{ gap: 15, flexWrap: 'wrap' }}>
            <div style={{ maxWidth: '350px', flexGrow: 1 }}>
              {SelectRefundType}
            </div>
            {RefundPercent}
            {RefundAmount}
          </div>
        </div>
      </div>
    );
  }
}

PurchaseRefund.propTypes = {
  onRef: PropTypes.func.isRequired,
  parkingType: PropTypes.oneOf(['booking', 'onDemand']).isRequired,
  onValidate: PropTypes.func.isRequired,
  serverError: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  locale: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  locale: _get(state, 'intl.locale', 'fr-FR'),
  serverError: _get(state, 'cpmFinance.purchase.error', null),
});

module.exports = connect(
  mapStateToProps,
)(PurchaseRefund);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/assets/purchases/PurchaseRefund.js